import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";

import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const NormalKeywordRecommendButton = ({
  licenseKey,
  category,
  checkType,
  source,
  onApply,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recommendation, setRecommendation] = useState(null);

  const { selectedCategory } = useSelector((state) => state.ruleFrame);
  const effectiveCategory = selectedCategory || category;

  const missingParams =
    !licenseKey || !effectiveCategory || !checkType || !source;

  const fetchRecommendation = useCallback(async () => {
    if (missingParams) return;

    setLoading(true);
    setError(null);
    setRecommendation(null);

    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/keywords/normalrecommend/`,
        {
          license_key: licenseKey,
          category: effectiveCategory,
          check_type: checkType,
          source: source,
        },
      );
      setRecommendation(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        // No recommendation available for this exact check_type/source pair.
        setRecommendation(null);
        setError(null);
      } else {
        setError(
          err.response?.data?.status ||
            err.response?.data?.message ||
            err.message ||
            "Failed to fetch recommendation.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [licenseKey, effectiveCategory, checkType, source, missingParams]);

  const handleOpen = () => {
    setOpen(true);
    fetchRecommendation();
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setRecommendation(null);
  };

  const handleApply = () => {
    if (!recommendation || !onApply) return;
    onApply(recommendation);
    handleClose();
  };

  // Parse the keywords string into AND-groups of OR-terms for display.
  const parseKeywords = (kwStr) => {
    if (!kwStr) return [];
    return kwStr
      .split(",")
      .map((g) =>
        g
          .split("+")
          .map((k) => k.trim())
          .filter(Boolean),
      )
      .filter((g) => g.length > 0);
  };

  const groups = recommendation ? parseKeywords(recommendation.keywords) : [];

  const tooltipTitle = missingParams
    ? "Select a category first"
    : `Suggest the most common "${checkType} in ${source}" keywords for this category`;

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span>
          <Button
            size="small"
            variant="outlined"
            onClick={handleOpen}
            disabled={disabled || missingParams}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontSize: "0.7rem",
              py: 0.4,
              px: 1,
              borderColor: "#7c4dff",
              color: "#7c4dff",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              "&:hover": {
                background: "#7c4dff",
                color: "#fff",
                borderColor: "#7c4dff",
              },
              "&:disabled": {
                borderColor: "#ccc",
                color: "#bbb",
              },
            }}
          >
            Suggest
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        PaperProps={{
          sx: {
            borderRadius: "12px",
            border: "1.5px solid #ede7f6",
            boxShadow: "0 8px 40px rgba(124,77,255,0.12)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #7c4dff 0%, #5c35cc 100%)",
            color: "#fff",
            py: 1.5,
            px: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "10px 10px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
              Recommended Keywords
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#fff" }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "#888" }} />
            <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
              Most commonly used{" "}
              <strong style={{ color: "#333" }}>{checkType}</strong> /{" "}
              <strong style={{ color: "#333" }}>{source}</strong> keywords for
              category:{" "}
              <strong style={{ color: "#333" }}>{effectiveCategory}</strong>
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Loading */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                py: 3,
              }}
            >
              <CircularProgress size={20} sx={{ color: "#7c4dff" }} />
              <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>
                Fetching recommendation�
              </Typography>
            </Box>
          )}

          {/* Error */}
          {!loading && error && (
            <Alert severity="error" sx={{ fontSize: "0.8rem" }}>
              {error}
            </Alert>
          )}

          {/* Recommendation content */}
          {!loading && recommendation && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                {recommendation.check_type && (
                  <Chip
                    label={`Check Type: ${recommendation.check_type}`}
                    size="small"
                    sx={{
                      background: "#ede7f6",
                      color: "#4527a0",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                    }}
                  />
                )}
                {recommendation.source && (
                  <Chip
                    label={`Source: ${recommendation.source}`}
                    size="small"
                    sx={{
                      background: "#e8f5e9",
                      color: "#2e7d32",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                    }}
                  />
                )}
              </Box>

              {/* Keywords visual breakdown */}
              {groups.length > 0 && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      color: "#7c4dff",
                      mb: 0.75,
                    }}
                  >
                    Keywords
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    {groups.map((group, gi) => (
                      <React.Fragment key={gi}>
                        {gi > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              px: 0.5,
                            }}
                          >
                            <Box
                              sx={{ height: 1, flex: 1, background: "#e0e0e0" }}
                            />
                            <Box
                              sx={{
                                px: 0.75,
                                py: 0.15,
                                borderRadius: "4px",
                                background: "#1a1a2e",
                                color: "#fff",
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                letterSpacing: "0.08em",
                              }}
                            >
                              AND
                            </Box>
                            <Box
                              sx={{ height: 1, flex: 1, background: "#e0e0e0" }}
                            />
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            p: 0.75,
                            borderRadius: "6px",
                            border: "1.5px solid #c5cae9",
                            background: "#f8f9ff",
                          }}
                        >
                          {group.map((kw, ki) => (
                            <React.Fragment key={ki}>
                              {ki > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    px: 0.5,
                                    py: 0.15,
                                    borderRadius: "4px",
                                    background: "#e8f5e9",
                                    color: "#2e7d32",
                                    fontSize: "0.6rem",
                                    fontWeight: 700,
                                    alignSelf: "center",
                                  }}
                                >
                                  OR
                                </Box>
                              )}
                              <Chip
                                label={kw}
                                size="small"
                                sx={{
                                  background: "#e8eaf6",
                                  color: "#283593",
                                  fontWeight: 500,
                                  fontSize: "0.78rem",
                                  height: 24,
                                  border: "1px solid #c5cae9",
                                }}
                              />
                            </React.Fragment>
                          ))}
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Usage stat if provided */}
              {recommendation.usage_count !== undefined && (
                <Box
                  sx={{
                    background: "#f3e5f5",
                    borderRadius: "6px",
                    px: 1.5,
                    py: 0.75,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontSize: "0.75rem", color: "#6a1b9a" }}>
                    Used by <strong>{recommendation.usage_count}</strong>{" "}
                    {recommendation.usage_count === 1
                      ? "customer"
                      : "customers"}{" "}
                    for this exact rule
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* No recommendation */}
          {!loading && !error && !recommendation && (
            <Typography
              sx={{
                fontSize: "0.8rem",
                color: "#888",
                textAlign: "center",
                py: 2,
              }}
            >
              No {checkType?.toLowerCase()} / {source?.toLowerCase()} keyword
              configuration found for this category yet.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleClose}
            sx={{
              fontSize: "0.78rem",
              borderColor: "#ccc",
              color: "#666",
              textTransform: "none",
              borderRadius: "6px",
            }}
          >
            Ignore & Close
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            disabled={!recommendation || loading}
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: "0.78rem",
              background: "linear-gradient(135deg, #7c4dff 0%, #5c35cc 100%)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "6px",
              fontWeight: 600,
              boxShadow: "0 2px 8px rgba(124,77,255,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #6a3de8 0%, #4a2ab0 100%)",
              },
            }}
          >
            Apply Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NormalKeywordRecommendButton;
